<template>
    <h5 class="subtitle is-5 mb-5">These settings affect the email reports being sent and their templates.</h5>

    <div class="field mb-5">
        <label class="label">Daily Report Time</label>
        <span>The time of day to email the daily legs report</span>
        <div class="control mt-2">
            <div class="select is-fullwidth">
                <select
                    :value="settings.dailyReportTime"
                    @change="handleDailyReportTimeChange"
                >
                    <option value="">Select an hour</option>
                    <option v-for="hour in 24" :key="hour - 1" :value="hour - 1">
                        {{ formatHour(hour - 1) }}
                    </option>
                </select>
            </div>
        </div>
        <p v-if="isTimeSettingSaving" class="help is-info">Saving...</p>
    </div>

    <SettingsInput
        label="Daily Report Recipients"
        description="The email addresses separated by semicolons to send the Daily Legs Report to"
        settingKey="dailyReportRecipients"
        placeholder="email1@stajets.com; email2@stajets.com"
        :validator="validateRecipients"
    />

    <SettingsInput
        label="Checkride Email"
        description="The email addresses separated by semicolons to send checkride legality certificate to"
        settingKey="checkrideEmails"
        placeholder="email1@stajets.com; email2@stajets.com"
        :validator="validateRecipients"
    />

    <SettingsInput
        label="Email Blacklist"
        description="The email addresses separated by semicolons to not send emails to"
        settingKey="emailBlacklist"
        placeholder="email1@stajets.com; email2@stajets.com"
        :validator="validateRecipients"
    />

    <SettingsInput
        label="Missing Data Notification (minutes)"
        description="The amount of minutes to wait before sending pilots a missing data email"
        :min="10"
        settingKey="missingDataNotificationTime"
        placeholder="Must be greater than 10 minutes"
        :validator="validateMissingDataTime"
    />

    <div class="field mb-5">
        <label class="label">Missing Data Email Template</label>
        <span>The template for the missing data email notification</span>

        <p class="help mb-2">
            Available variables: <br>
            <button 
                v-for="variable in variables" 
                :key="variable.code"
                class="button is-small mr-2 mb-2"
                @click="copyToClipboard(variable.code)"
            >
                <code>{{ variable.code }}</code> - {{ variable.description }}
            </button>
        </p>
        
        <div class="control mt-2" style="height: 300px;">
            <QuillEditor
                ref="quillEditor"
                theme="snow"
                toolbar="full"
                @ready="handleEditorReady"
            />
        </div>
        <p v-if="isTemplateSettingSaving" class="help is-info">Saving...</p>
    </div>
    
</template>

<script>
import { useSettings } from '@/lib/settings';
import { useDebounce } from '@/composables/useDebounce';
import { ref, onMounted, watch } from 'vue';
import SettingsInput from './SettingsInput.vue';
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
    components: { SettingsInput, QuillEditor },
    setup() {
        const { settings, setSetting, getSetting } = useSettings();
        const { debounce } = useDebounce();
        const isTimeSettingSaving = ref(false);
        const isTemplateSettingSaving = ref(false);
        const quillEditor = ref(null);
        // Only save user edits, and only after the stored template has been
        // loaded into the editor - a programmatic load must never trigger
        // a save, or an unloaded editor overwrites the template with ''.
        // pasteHTML (not setHTML) is required here: setHTML writes the DOM
        // directly, and Quill reports mutation-observer changes as
        // source 'user', which would re-save on every page open.
        const isTemplateLoaded = ref(false);

        const loadTemplateIntoEditor = () => {
            const template = getSetting('missingDataEmailTemplate');
            if (template === undefined || isTemplateLoaded.value) return;
            // The component ref exists as soon as the DOM mounts, but pasting
            // before the inner Quill instance is ready silently no-ops - only
            // getQuill() confirms the editor can actually receive content.
            if (!quillEditor.value?.getQuill()) return;
            quillEditor.value.pasteHTML(template);
            isTemplateLoaded.value = true;
        };

        onMounted(loadTemplateIntoEditor);
        watch(() => getSetting('missingDataEmailTemplate'), loadTemplateIntoEditor);

        const formatHour = (hour) => {
            const period = hour < 12 ? 'AM' : 'PM';
            const displayHour = hour % 12 || 12;
            return `${displayHour.toString().padStart(2, '0')}:00 ${period}`;
        };

        const validateRecipients = (value) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            const emails = value.split(';').map(email => email.trim());
            const validEmails = emails.every(email => emailRegex.test(email));
            
            return validEmails ? null : 'Invalid email format. Please check the email addresses.';
        };

        const validateMissingDataTime = (value) => {
            return value >= 10 ? null : 'Missing data notification time must be at least 10 minutes.';
        };

        const handleDailyReportTimeChange = async (e) => {
            isTimeSettingSaving.value = true;
            settings.dailyReportTime = e.target.value;
            await setSetting('dailyReportTime', e.target.value, 'number');
            isTimeSettingSaving.value = false;
        };

        const handleTemplateChange = debounce('template', async () => {
            if (!isTemplateLoaded.value) return;
            try {
                isTemplateSettingSaving.value = true;
                const htmlContent = quillEditor.value?.getQuill()?.root.innerHTML || '';
                console.log('HTML content:', htmlContent);
                await setSetting('missingDataEmailTemplate', htmlContent, 'string');
                console.log('Template saved successfully:', htmlContent);
            } catch (error) {
                console.error('Template save error:', error);
            } finally {
                isTemplateSettingSaving.value = false;
            }
        });

        const handleEditorReady = (quill) => {
            quill.on('text-change', (delta, oldDelta, source) => {
                if (source === 'user') handleTemplateChange();
            });
            loadTemplateIntoEditor();
        };

        const variables = [
            { code: '{PilotFirstName}', description: "Pilot's first name" },
            { code: '{PilotLastName}', description: "Pilot's last name" },
            { code: '{TailNumber}', description: 'Aircraft tail number' },
            { code: '{DepartingDate}', description: 'Flight departure date' },
            { code: '{DepartingAirport}', description: 'Departure airport' },
            { code: '{ArrivingAirport}', description: 'Arrival airport' },
            { code: '{MissingData}', description: 'List of missing data fields' },
            { code: '{LevelFlightLink}', description: 'Link to trip in Level Flight' },
        ];

        const copyToClipboard = (text) => {
            navigator.clipboard.writeText(text);
        };

        return {
            settings,
            formatHour,
            setSetting,
            validateRecipients,
            validateMissingDataTime,
            isTimeSettingSaving,
            isTemplateSettingSaving,
            handleDailyReportTimeChange,
            handleEditorReady,
            quillEditor,
            variables,
            copyToClipboard,
        };
    },
}
</script>

<style scoped>
.button code {
    background: none;
    padding: 0;
}
</style>